import React, { ChangeEvent, FC } from 'react';
import { Field, TextInput } from '@kontentino/ui';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import DateUtils from 'app/utils/date';

interface Props {
  error: {
    startDate: boolean;
    endDate: boolean;
  };
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  onStartDateChange: (date: Dayjs | null) => void;
  onEndDateChange: (date: Dayjs | null) => void;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  dataCy?: {
    startDate?: string;
    endDate?: string;
  };
}

const DatePickerRangeInputs: FC<Props> = ({
  error,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  minDate,
  maxDate,
  dataCy,
}) => {
  const { t } = useTranslation();

  const handleEndDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      if (dayjs(new Date(e.target.value)).isBefore(dayjs(startDate))) {
        onStartDateChange?.(dayjs(e.target.value));
        onEndDateChange?.(null);
      } else {
        onEndDateChange?.(dayjs(e.target.value));
      }
    } else {
      onEndDateChange?.(null);
    }
  };

  const handleStartDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      if (dayjs(dayjs(e.target.value)).isAfter(dayjs(endDate))) {
        onStartDateChange?.(dayjs(e.target.value));
        onEndDateChange?.(null);
      } else {
        onStartDateChange?.(dayjs(e.target.value));
      }
    } else {
      onStartDateChange?.(null);
      onEndDateChange?.(null);
    }
  };

  return (
    <>
      <Field.Group>
        <Field.Label required>{t('start')}</Field.Label>
        <TextInput
          onChange={handleStartDateChange}
          type="date"
          value={DateUtils.toDateString(dayjs(startDate))}
          max={DateUtils.toDateString(dayjs(maxDate))}
          min={minDate ? DateUtils.toDateString(dayjs(minDate)) : undefined}
          error={!error.startDate}
          data-cy={dataCy?.startDate}
        />
        <Field.Error>{!error.startDate && t('invalidDate')}</Field.Error>
      </Field.Group>
      <Field.Group>
        <Field.Label required>{t('end')}</Field.Label>
        <TextInput
          disabled={!startDate}
          type="date"
          onChange={handleEndDateChange}
          value={DateUtils.toDateString(dayjs(endDate))}
          max={maxDate ? DateUtils.toDateString(dayjs(maxDate)) : undefined}
          min={
            startDate && !endDate
              ? DateUtils.toDateString(dayjs(startDate))
              : minDate
              ? DateUtils.toDateString(dayjs(minDate))
              : undefined
          }
          error={!error.endDate}
          data-cy={dataCy?.endDate}
        />
        <Field.Error>{!error.endDate && t('invalidDate')}</Field.Error>
      </Field.Group>
    </>
  );
};

export default DatePickerRangeInputs;
